import { z } from 'zod';

/**
 * Must stay aligned with {@link projectFileClassificationTypeSchema} in
 * project-file-batch.model.ts (no import here — avoids circular dependency).
 */
export const projectFileSegmentClassificationTypeSchema = z.enum([
  'blueprint',
  'building_rendering',
  'project_description',
  'project_brief',
  'area_schedule',
  'bill_of_materials',
  'cost_estimate',
  'technical_spec',
  'title_block',
  'unknown',
]);
export type ProjectFileSegmentClassificationType = z.infer<
  typeof projectFileSegmentClassificationTypeSchema
>;

/** Bump when `extraction_output` JSON shape changes incompatibly (e.g. blueprint `buildings[]`). */
export const PROJECT_FILE_EXTRACTION_SCHEMA_VERSION = 2 as const;

/**
 * One successful field (or nested object) with provenance metadata per epic / DEV-3192.
 * Use for leaf values; objects may nest more `extractedFieldSchema` or plain JSON.
 */
export const extractedFieldSchema = z.object({
  value: z.unknown(),
  confidence: z.number().min(0).max(1),
  source_clue: z.string().optional(),
  is_estimated: z.boolean().optional(),
});
export type ExtractedField = z.infer<typeof extractedFieldSchema>;

/**
 * Single segment row after extraction: one classification row → one parallel agent result.
 */
const projectFileSegmentExtractionRowBaseSchema = z.object({
  classificationSegmentIndex: z.number().int().nonnegative(),
  classificationType: projectFileSegmentClassificationTypeSchema,
  sourceFileId: z.string(),
  /** Synthetic id for provenance (e.g. `${fileId}:${segmentIndex}`) until segment UUIDs exist. */
  sourceSubDocumentId: z.string(),
});

const projectFileSegmentExtractionRowOkSchema =
  projectFileSegmentExtractionRowBaseSchema.extend({
    ok: z.literal(true),
    /** Type-specific payload when `ok`; shape varies by {@link classificationType}. */
    data: z.record(z.string(), z.unknown()),
  });

const projectFileSegmentExtractionRowErrorSchema =
  projectFileSegmentExtractionRowBaseSchema.extend({
    ok: z.literal(false),
    error: z.string(),
  });

export const projectFileSegmentExtractionRowSchema = z.discriminatedUnion(
  'ok',
  [
    projectFileSegmentExtractionRowOkSchema,
    projectFileSegmentExtractionRowErrorSchema,
  ],
);
export type ProjectFileSegmentExtractionRow = z.infer<
  typeof projectFileSegmentExtractionRowSchema
>;

/**
 * Versioned merge of all segment extractors for `project_files.extraction_output`.
 */
export const projectFileExtractionOutputV1Schema = z.object({
  schemaVersion: z.literal(PROJECT_FILE_EXTRACTION_SCHEMA_VERSION),
  /** Model id used for extraction calls (OpenAI), when available. */
  model: z.string().optional(),
  segments: z.array(projectFileSegmentExtractionRowSchema),
});
export type ProjectFileExtractionOutputV1 = z.infer<
  typeof projectFileExtractionOutputV1Schema
>;

/** Legacy Phase-1 stub or unknown blobs in JSONB accepted until migration. */
const projectFileExtractionLegacySchema = z.record(z.string(), z.unknown());

export const projectFileExtractionOutputMergedSchema = z.union([
  projectFileExtractionOutputV1Schema,
  projectFileExtractionLegacySchema,
]);

export type ProjectFileExtractionOutput = z.infer<
  typeof projectFileExtractionOutputMergedSchema
>;

export const isProjectFileExtractionOutputV1 = (
  v: unknown,
): v is ProjectFileExtractionOutputV1 =>
  projectFileExtractionOutputV1Schema.safeParse(v).success;

/** Build synthetic sub-document id for extraction provenance. */
export const makeSourceSubDocumentId = (
  fileId: string,
  segmentIndex: number,
): string => `${fileId}:${segmentIndex}`;

/**
 * True if merged output has at least one successful segment row (for file `done` vs `failed`).
 */
export const extractionOutputHasSuccessfulSegment = (
  output: ProjectFileExtractionOutput,
): boolean => {
  if (!isProjectFileExtractionOutputV1(output)) return true;
  return output.segments.some((s) => s.ok);
};

/** Tag keys for `data` blobs per {@link ProjectFileClassificationType} (documentation). */
export const PROJECT_FILE_EXTRACTION_DATA_KIND = {
  blueprint: 'blueprint',
  building_rendering: 'building_rendering',
  project_description: 'project_description',
  project_brief: 'project_brief',
  area_schedule: 'area_schedule',
  bill_of_materials: 'bill_of_materials',
  cost_estimate: 'cost_estimate',
  technical_spec: 'technical_spec',
  title_block: 'title_block',
  unknown: 'unknown',
} as const satisfies Record<ProjectFileSegmentClassificationType, string>;
